import { sanitizeUpload } from "./slug";
import {
  runAdvertisingVideoUpload,
  runS3Upload,
  runVttUpload,
} from "./uploadEngine";
import EncoderAPI from "../api/EncoderAPI";
import OfferAPI from "../api/OfferAPI";

import type { QueryKey } from "@tanstack/react-query";

import type { UploadEngineResult } from "./uploadEngine";
import type { UploadJob, UploadJobKind } from "../contexts/uploadQueue.types";

export interface UploadRunnerContext {
  onProgress: (progress: number) => void;
  signal: AbortSignal;
}

export async function runUploadJob(
  job: UploadJob,
  { onProgress, signal }: UploadRunnerContext,
): Promise<UploadEngineResult> {
  switch (job.kind) {
    case "mediaVtt":
      return runVttUpload(job.file, {
        mediaId: job.mediaId,
        onProgress,
        signal,
      });
    case "mediaSource":
      return runS3Upload(
        job.file,
        job.uploadedFilename ?? sanitizeUpload(job.displayName),
        { mediaId: job.mediaId, onProgress, signal },
      );
    case "advertisingVideo":
      return runAdvertisingVideoUpload(job.file, {
        campaignUuid: job.campaignUuid,
        languageUuid: job.languageUuid,
        projectSlug: job.projectSlug,
        onProgress,
        signal,
      });
    default: {
      // Exhaustiveness guard: a new UploadJobKind must be routed explicitly.
      const exhaustiveCheck: never = job;

      return exhaustiveCheck;
    }
  }
}

export function getInvalidateQueryKeyForJob(job: UploadJob): QueryKey {
  switch (job.kind) {
    case "mediaSource":
    case "mediaVtt":
      return EncoderAPI.generateQuery({
        fn: EncoderAPI.getSource,
        params: [job.mediaId],
      }).queryKey;
    case "advertisingVideo":
      return OfferAPI.generateQuery({
        fn: OfferAPI.getSingleAdvertising,
        params: [{ uuid: job.campaignUuid, projectSlug: job.projectSlug }],
      }).queryKey;
    default: {
      const exhaustiveCheck: never = job;

      return exhaustiveCheck;
    }
  }
}

/**
 * Whether the queue must wait for the invalidated query to have refetched
 * before announcing the job as `completed`.
 */
export function shouldAwaitRefreshBeforeCompletion(job: UploadJob): boolean {
  switch (job.kind) {
    case "mediaSource":
    case "mediaVtt":
      // The media page keeps polling for the source to appear server side, so
      // it needs `completed` as early as possible to take over from the queue.
      return false;
    case "advertisingVideo":
      // Nothing follows the upload here: `completed` is the campaign's only
      // signal, so the cache must already carry the video when it is announced.
      return true;
    default: {
      const exhaustiveCheck: never = job;

      return exhaustiveCheck;
    }
  }
}

export function getInitialUploadedFilename(
  kind: UploadJobKind,
  file: File,
): string | null {
  switch (kind) {
    case "mediaSource":
      return sanitizeUpload(file.name);
    case "mediaVtt":
      return null;
    case "advertisingVideo":
      // The server names the file and returns it in the upload response.
      return null;
    default: {
      const exhaustiveCheck: never = kind;

      return exhaustiveCheck;
    }
  }
}
